/**
 * Nyhetsbrev-popup — to varianter, tre steg.
 *
 * Trigger: N sekunder etter at siden er klar, ELLER X % scrolldybde — det som
 * kommer først. Begge verdier styres fra section-schemaet via data-attributter.
 *
 * VIKTIG (rettet 2026-08-11): scroll-lytteren armes IKKE før scrollArmMs har
 * gått. Uten den sperren vant scroll alltid racet — målt fyring 1,5–5,3 sek
 * etter landing på alle sidetyper, som gjorde delay-innstillingen til død kode
 * og avbrøt folk før de rakk å lese noe. #open() har i tillegg et hardt gulv
 * mot samme tidspunkt, så ingen trigger kan komme forbi det.
 *
 * Suppresjon:
 *   - permanent etter innsending
 *   - N dagers cooldown etter avvisning/visning
 *   - localStorage blokkert (in-app-nettleser, privat modus) — uten
 *     skrivetilgang finnes hverken cooldown eller innsendt-sperre
 *   - /cart, /checkout, /account
 *   - variant A: kun nye besøkende (hvis slått på)
 *   - Shopify customerPrivacy: userCanBeTracked() === false
 *   - (Liquid-nivå: innloggede med tagg 'newsletter', og excluded_paths)
 *
 * QA: legg til ?mhpopup=force i URL-en for å omgå ALL suppresjon. Force-modus
 * skriver ikke cooldown/submitted til localStorage, så du kan teste gjentatte
 * ganger. Nødvendig fordi localStorage deles mellom live- og preview-tema.
 *
 * Markup-kontrakt: sections/newsletter-popup.liquid
 * Dialog-komponent: assets/dialog.js (Horizon stock)
 */

const STORAGE_SHOWN = 'mh_newsletter_shown';
const STORAGE_SUBMITTED = 'mh_newsletter_submitted';
const STORAGE_FIRST_SEEN = 'mh_first_seen';
const SESSION_NEW_VISITOR = 'mh_new_visitor';
const SUPPRESS_PATH_RE = /^\/(cart|checkout|account)(\/|$|\?)/i;
const CONSENT_WAIT_MAX_MS = 3_000;
const DAY_MS = 24 * 60 * 60 * 1000;

/* ───────── localStorage/sessionStorage-hjelpere (privat modus-trygge) ───────── */

function lsGet(key) {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function lsSet(key, value) {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* blokkert — akseptabel degradering */
  }
}

function ssGet(key) {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function ssSet(key, value) {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    /* ignorer */
  }
}

/**
 * Kan vi faktisk BÅDE lese og skrive localStorage?
 *
 * lsGet() returnerer null både når nøkkelen mangler og når storage er
 * blokkert, og de to tilstandene må skilles: uten skrivetilgang kan vi
 * verken huske innsending eller cooldown, og hver sidevisning ville da
 * telle som «aldri sett popupen før». I en restriktiv in-app-nettleser
 * (Facebook/Instagram) eller privat modus betyr det ny popup på hver
 * eneste side — også til folk som allerede har meldt seg på.
 *
 * Safari i privat modus tillot historisk getItem men kastet på setItem,
 * så probe-en må skrive, lese tilbake og rydde opp etter seg.
 * Resultatet caches — dette kalles per sidevisning, ikke i en løkke.
 */
let storageProbeResult = null;

function storageWorks() {
  if (storageProbeResult !== null) return storageProbeResult;
  try {
    const probeKey = '__mh_storage_probe__';
    localStorage.setItem(probeKey, '1');
    storageProbeResult = localStorage.getItem(probeKey) === '1';
    localStorage.removeItem(probeKey);
  } catch {
    storageProbeResult = false;
  }
  return storageProbeResult;
}

/**
 * Ny besøkende = ingen mh_first_seen i localStorage ved aller første sidevisning.
 * Flagget settes umiddelbart, så vi speiler statusen i sessionStorage for at
 * resten av besøket skal telle som "ny" og ikke vippe til "tilbakevendende"
 * på sidevisning nummer to.
 */
function isNewVisitor() {
  if (ssGet(SESSION_NEW_VISITOR) === '1') return true;
  if (ssGet(SESSION_NEW_VISITOR) === '0') return false;

  const firstSeen = lsGet(STORAGE_FIRST_SEEN);
  if (!firstSeen) {
    lsSet(STORAGE_FIRST_SEEN, Date.now().toString());
    ssSet(SESSION_NEW_VISITOR, '1');
    return true;
  }
  ssSet(SESSION_NEW_VISITOR, '0');
  return false;
}

class NewsletterPopup extends HTMLElement {
  #timer = null;
  #armTimer = null;
  #earliestOpenAt = 0;
  #scrollHandler = null;
  #dialogEl = null;
  #backdropGuard = null;
  #overflowHandler = null;
  #opened = false;
  #force = false;
  #dialogComponent = null;
  #form = null;
  #submitButton = null;
  #choice = '';
  #choiceTag = '';
  #cfg = {};

  async connectedCallback() {
    this.#cfg = {
      variant: this.dataset.variant || 'a',
      pageType: this.dataset.pageType || '',
      pagePath: this.dataset.pagePath || '',
      delayMs: Number(this.dataset.delayMs) || 12_000,
      scrollArmMs: Number(this.dataset.scrollArmMs) || 8_000,
      scrollThreshold: Number(this.dataset.scrollThreshold) || 0.5,
      cooldownMs: (Number(this.dataset.cooldownDays) || 30) * DAY_MS,
      newOnly: this.dataset.newOnly === 'true',
      baseTags: this.dataset.baseTags || 'newsletter,king-popup',
      code: this.dataset.discountCode || '',
    };

    try {
      this.#force = new URLSearchParams(window.location.search).get('mhpopup') === 'force';
    } catch {
      this.#force = false;
    }

    if (!this.#force) {
      if (this.#shouldSuppress()) {
        this.remove();
        return;
      }

      const canTrack = await this.#waitForConsent();
      if (!canTrack) {
        this.remove();
        return;
      }
    }

    this.#dialogComponent = this.querySelector('dialog-component');
    this.#form = this.querySelector('form');
    this.#submitButton = this.querySelector('[data-submit-button]');

    if (!this.#dialogComponent) return;

    this.#bindEvents();
    this.#armTriggers();
  }

  disconnectedCallback() {
    this.#teardownTriggers();
    if (this.#backdropGuard && this.#dialogComponent) {
      this.#dialogComponent.removeEventListener('click', this.#backdropGuard, { capture: true });
    }
    if (this.#overflowHandler) {
      this.#dialogEl?.removeEventListener('scroll', this.#overflowHandler);
      window.removeEventListener('resize', this.#overflowHandler);
    }
    this.#backdropGuard = null;
    this.#overflowHandler = null;
  }

  /* ───────── Suppresjon ───────── */

  #shouldSuppress() {
    // Blokkert storage = undertrykk. Vi kan ikke skille «har aldri sett den»
    // fra «husker ingenting», og feil vei her er å vise popupen på nytt ved
    // hvert eneste sidebytte. ?mhpopup=force hopper over hele denne metoden,
    // så QA fungerer fortsatt i en nettleser uten storage.
    if (!storageWorks()) return true;

    if (lsGet(STORAGE_SUBMITTED) === '1') return true;

    const shownTs = Number(lsGet(STORAGE_SHOWN));
    if (shownTs && Date.now() - shownTs < this.#cfg.cooldownMs) return true;

    if (SUPPRESS_PATH_RE.test(window.location.pathname)) return true;

    if (this.#cfg.newOnly && !isNewVisitor()) return true;

    return false;
  }

  async #waitForConsent() {
    const start = Date.now();
    while (Date.now() - start < CONSENT_WAIT_MAX_MS) {
      const api = window.Shopify?.customerPrivacy;
      if (api?.userCanBeTracked) {
        try {
          return api.userCanBeTracked();
        } catch {
          return true;
        }
      }
      await new Promise((r) => setTimeout(r, 100));
    }
    // Privacy-API-et lastet aldri — fortsett med default-permissiv oppførsel.
    return true;
  }

  /* ───────── Triggere ───────── */

  #armTriggers() {
    const start = () => {
      // Hardt gulv: uansett hvilken trigger som fyrer, aldri før dette.
      this.#earliestOpenAt = Date.now() + this.#cfg.scrollArmMs;

      this.#timer = setTimeout(() => this.#open('timer'), this.#cfg.delayMs);

      // Scroll er et ENGASJEMENTS-signal, ikke et ankomstsignal. Den armes
      // først etter scrollArmMs, ellers vinner den alltid racet mot timeren.
      this.#armTimer = setTimeout(() => {
        this.#scrollHandler = () => {
          const max = document.documentElement.scrollHeight - window.innerHeight;
          if (max <= 0) return;
          if (window.scrollY / max >= this.#cfg.scrollThreshold) this.#open('scroll');
        };
        window.addEventListener('scroll', this.#scrollHandler, { passive: true });
      }, this.#cfg.scrollArmMs);
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', start, { once: true });
    } else {
      start();
    }
  }

  #teardownTriggers() {
    clearTimeout(this.#timer);
    clearTimeout(this.#armTimer);
    if (this.#scrollHandler) window.removeEventListener('scroll', this.#scrollHandler);
    this.#timer = null;
    this.#armTimer = null;
    this.#scrollHandler = null;
  }

  /* ───────── Analytics ───────── */

  /**
   * Sender event via tre kanaler — samme mønster som
   * snippets/mh-track-product-callouts.liquid. På minhundpet.no er det
   * Shopify.analytics.publish som faktisk når GA4 (via Custom Pixel);
   * dataLayer og gtag er defensive fallbacks hvis oppsettet endres.
   */
  #fire(eventName, extra = {}) {
    const payload = {
      popup_variant: this.#cfg.variant,
      page_type: this.#cfg.pageType,
      page_path: this.#cfg.pagePath,
      step1_choice: this.#choice || '(none)',
      ...extra,
    };

    try {
      if (window.Shopify?.analytics?.publish) {
        window.Shopify.analytics.publish(eventName, payload);
      }
    } catch {
      /* ignorer */
    }
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign({ event: eventName }, payload));
    } catch {
      /* ignorer */
    }
    try {
      if (typeof window.gtag === 'function') window.gtag('event', eventName, payload);
    } catch {
      /* ignorer */
    }
    try {
      if (typeof window.clarity === 'function') {
        window.clarity('set', 'mh_popup_variant', this.#cfg.variant);
      }
    } catch {
      /* ignorer */
    }
  }

  /* ───────── Hendelser ───────── */

  #bindEvents() {
    this.querySelectorAll('[data-close], [data-decline]').forEach((el) => {
      el.addEventListener('click', (e) => {
        e.preventDefault();
        this.#fire('mh_popup_dismiss', { dismiss_method: el.hasAttribute('data-decline') ? 'decline' : 'close' });
        this.#markShown();
        this.#close();
      });
    });

    this.querySelectorAll('[data-choice]').forEach((el) => {
      el.addEventListener('click', () => this.#selectChoice(el));
    });

    const backBtn = this.querySelector('[data-back]');
    if (backBtn) backBtn.addEventListener('click', () => this.#goToStep(1));

    if (this.#form) this.#form.addEventListener('submit', (e) => this.#handleSubmit(e));

    const copyBtn = this.querySelector('[data-copy]');
    if (copyBtn) copyBtn.addEventListener('click', () => this.#copyCode(copyBtn));

    const shopBtn = this.querySelector('[data-shop-now]');
    if (shopBtn) {
      shopBtn.addEventListener('click', () => {
        this.#fire('mh_popup_shop_click');
        this.#close();
      });
    }

    // Esc lukker via dialog.js — logg det som avvisning.
    const dialog = this.querySelector('dialog');
    this.#dialogEl = dialog;
    if (dialog) {
      dialog.addEventListener('close', () => {
        if (this.#opened && lsGet(STORAGE_SUBMITTED) !== '1') this.#markShown();
      });

      // Scroll-affordanse: marker at det finnes mer innhold under folden.
      this.#overflowHandler = () => this.#syncOverflow();
      dialog.addEventListener('scroll', this.#overflowHandler, { passive: true });
      window.addEventListener('resize', this.#overflowHandler, { passive: true });
    }

    this.#blockBackdropDismiss();
  }

  /**
   * Klikk-utenfor-for-å-lukke er fjernet for denne popupen.
   *
   * Bakteppet var 17,3× større enn alle tre valgene til sammen på desktop og
   * 2,8× på mobil — den desidert enkleste handlingen på skjermen, og på mobil
   * ligger det nøyaktig der tommelen står midt i en scroll. Brukeren har
   * fortsatt tre bevisste veier ut: lukk-X, «Nei takk» og Esc.
   *
   * dialog.js er delt av 12 andre flater (søk, filtre, buy-buttons, password),
   * så den filen røres ikke. I stedet fanges bakteppe-klikk i CAPTURE-fasen på
   * <dialog-component>, før dialog.js sin bubble-handler ser dem. Klikk INNI
   * dialogboksen slippes urørt gjennom, ellers ville valgknappene sluttet å
   * virke. Logikken speiler utilities.js#isClickedOutside.
   */
  #blockBackdropDismiss() {
    const host = this.#dialogComponent;
    const dialog = this.#dialogEl;
    if (!host || !dialog) return;

    this.#backdropGuard = (event) => {
      if (!dialog.open) return;
      const insideBox =
        event.target instanceof Element && !(event.target instanceof HTMLDialogElement)
          ? dialog.contains(event.target)
          : (() => {
              const { left, right, top, bottom } = dialog.getBoundingClientRect();
              return (
                event.clientX >= left &&
                event.clientX <= right &&
                event.clientY >= top &&
                event.clientY <= bottom
              );
            })();

      if (!insideBox) event.stopPropagation();
    };

    host.addEventListener('click', this.#backdropGuard, { capture: true });
  }

  /** Setter data-overflow/data-at-end på dialogen så CSS kan vise scroll-hint. */
  #syncOverflow() {
    const d = this.#dialogEl;
    if (!d) return;
    const overflows = d.scrollHeight - d.clientHeight > 4;
    d.setAttribute('data-overflow', overflows ? 'true' : 'false');
    d.setAttribute('data-at-end', d.scrollTop + d.clientHeight >= d.scrollHeight - 6 ? 'true' : 'false');
  }

  /* ───────── Åpne / lukke ───────── */

  #open(trigger) {
    if (this.#opened) return;
    // Gulv mot for tidlig avbrudd — gjelder alle triggere.
    if (this.#earliestOpenAt && Date.now() < this.#earliestOpenAt) return;
    this.#opened = true;
    this.#teardownTriggers();

    try {
      this.#dialogComponent.showDialog();
    } catch (err) {
      console.warn('[newsletter-popup] showDialog feilet', err);
      return;
    }

    // showDialog() kjører showModal() inne i en rAF — vent på layout før måling.
    requestAnimationFrame(() => requestAnimationFrame(() => this.#syncOverflow()));

    this.#fire('mh_popup_view', { trigger_type: trigger });
  }

  #close() {
    try {
      this.#dialogComponent?.closeDialog();
    } catch {
      /* ignorer */
    }
  }

  #markShown() {
    if (this.#force) return;
    lsSet(STORAGE_SHOWN, Date.now().toString());
  }

  /* ───────── Steg ───────── */

  #selectChoice(el) {
    this.#choice = el.dataset.choice || '';
    this.#choiceTag = el.dataset.choiceTag || '';

    const echo = this.querySelector('[data-choice-echo]');
    if (echo) echo.textContent = el.dataset.choiceEchoText || '';

    const tagsInput = this.querySelector('[data-tags-input]');
    if (tagsInput) {
      tagsInput.value = this.#choiceTag
        ? `${this.#cfg.baseTags},${this.#choiceTag}`
        : this.#cfg.baseTags;
    }

    this.#fire('mh_popup_step1');
    this.#goToStep(2);
  }

  #goToStep(n) {
    this.querySelectorAll('[data-step]').forEach((step) => {
      const isTarget = step.dataset.step === String(n);
      step.hidden = !isTarget;
      step.classList.toggle('mh-nl__step--active', isTarget);
    });

    const dialog = this.querySelector('dialog');
    if (dialog) dialog.scrollTop = 0;
    requestAnimationFrame(() => this.#syncOverflow());

    if (n === 2) {
      setTimeout(() => {
        const input = this.querySelector('input[type="email"]');
        if (input) input.focus({ preventScroll: true });
      }, 60);
    }
  }

  /* ───────── Innsending ───────── */

  /**
   * Sender skjemaet via skjult iframe — omgår fetch/CORS/CSRF-problemene som
   * gjør at Shopifys /contact-endepunkt avviser AJAX-POST (403 uten
   * authenticity_token). Optimistisk overgang til steg 3 rett etter at
   * forespørselen er sendt.
   */
  #handleSubmit(event) {
    event.preventDefault();

    const errorEl = this.querySelector('[data-error]');
    const labelEl = this.querySelector('[data-submit-label]');
    const loadingEl = this.querySelector('[data-submit-loading]');
    const input = this.#form.querySelector('input[type="email"]');

    if (errorEl) {
      errorEl.hidden = true;
      errorEl.textContent = '';
    }
    if (input) input.setAttribute('aria-invalid', 'false');

    if (!input?.value || !input.checkValidity()) {
      this.#showError('Skriv inn en gyldig e-postadresse.');
      input?.setAttribute('aria-invalid', 'true');
      input?.focus();
      return;
    }

    this.#submitButton.disabled = true;
    if (labelEl) labelEl.hidden = true;
    if (loadingEl) loadingEl.hidden = false;

    const iframeName = `mh-nl-target-${Date.now()}`;
    const iframe = document.createElement('iframe');
    iframe.name = iframeName;
    iframe.setAttribute('aria-hidden', 'true');
    iframe.setAttribute('tabindex', '-1');
    iframe.style.cssText = 'position:absolute;width:0;height:0;border:0;opacity:0;pointer-events:none;';
    document.body.appendChild(iframe);

    this.#form.target = iframeName;
    try {
      this.#form.submit();
    } catch (err) {
      console.warn('[newsletter-popup] form.submit() feilet', err);
      this.#showError('Noe gikk galt. Prøv igjen om litt.');
      this.#submitButton.disabled = false;
      if (labelEl) labelEl.hidden = false;
      if (loadingEl) loadingEl.hidden = true;
      iframe.remove();
      return;
    }

    setTimeout(() => this.#onSuccess(), 200);
    setTimeout(() => iframe.remove(), 30_000);
  }

  #showError(message) {
    const errorEl = this.querySelector('[data-error]');
    if (!errorEl) return;
    errorEl.textContent = message;
    errorEl.hidden = false;
  }

  #onSuccess() {
    if (!this.#force) {
      lsSet(STORAGE_SUBMITTED, '1');
      lsSet(STORAGE_SHOWN, Date.now().toString());
    }

    this.#fire('mh_popup_submit', { discount_code: this.#cfg.code });
    this.#goToStep(3);
  }

  /* ───────── Kopier kode ───────── */

  async #copyCode(button) {
    const code = this.querySelector('[data-code]')?.textContent?.trim();
    if (!code) return;

    try {
      await navigator.clipboard.writeText(code);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = code;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand('copy');
      } catch {
        /* gi opp */
      }
      ta.remove();
    }

    this.#fire('mh_popup_copy_code', { discount_code: code });

    button.dataset.copied = 'true';
    const label = button.querySelector('[data-copy-label]');
    const done = button.querySelector('[data-copy-done]');
    if (label) label.hidden = true;
    if (done) done.hidden = false;

    setTimeout(() => {
      button.dataset.copied = 'false';
      if (label) label.hidden = false;
      if (done) done.hidden = true;
    }, 2000);
  }
}

if (!customElements.get('newsletter-popup')) {
  customElements.define('newsletter-popup', NewsletterPopup);
}
